import { api, qs } from '../lib/api';

/**
 * Everything the panel reads from /v1/admin, with the shapes the API sends.
 * Keep these in step with slate-api/src/routes/admin.ts.
 */

export type Role = 'user' | 'admin' | 'owner';
export type Seat = 'free' | 'paid';
export type SubscriptionStatus = 'incomplete' | 'trialing' | 'active' | 'past_due' | 'canceled';
export type VideoStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AdminMe {
  id: string;
  email: string;
  name: string | null;
}

export interface DailyPoint {
  day: string;
  count: number;
}

export interface Stats {
  users: { total: number; last7d: number; last30d: number };
  orgs: { total: number; byStatus: Partial<Record<SubscriptionStatus, number>> };
  videos: {
    total: number;
    last7d: number;
    byStatus: Partial<Record<VideoStatus, number>>;
    totalDurationSec: number;
    totalBytes: number | null;
  };
  featureRequests: { open: number };
  admins: { total: number };
  daily: { signups: DailyPoint[]; recordings: DailyPoint[] };
}

export interface OrgRef {
  id: string;
  name: string;
  slug: string;
}

export interface UserRef {
  id: string;
  email: string;
  name: string | null;
}

/**
 * Premium handed out from this panel, with no Stripe subscription behind it.
 * Null where none was ever given; `active` is false for one that has lapsed,
 * which is kept so the panel can still say what happened and when.
 */
export interface Comp {
  at: string;
  until: string | null;
  reason: string | null;
  by: string | null;
  /** Teams only: how many seats the grant covers. Null on a person's grant. */
  seats: number | null;
  active: boolean;
}

/** A team's seats, split by who pays for them. Comped ones sit on top. */
export interface SeatBreakdown {
  paid: number;
  comped: number;
  total: number;
}

/** Whether this person is on the paid plan, and what puts them there. */
export interface Access {
  premium: boolean;
  source: 'comp' | 'seat' | 'none';
  comp: Comp | null;
}

export interface MembershipRow {
  role: Role;
  seat: Seat;
  org: OrgRef & {
    /** What Stripe last said. The override is `comp`, kept separate on purpose. */
    subscriptionStatus: SubscriptionStatus;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    comp: Comp | null;
    /** Whether the plan counts as live once the override is taken into account. */
    planLive: boolean;
  };
}

/** What a grant leaves a team with. Returned by both team comp endpoints. */
export interface OrgComp {
  comp: Comp | null;
  planLive: boolean;
  seats: SeatBreakdown;
}

export interface UserRow extends UserRef {
  avatarUrl: string | null;
  createdAt: string;
  _count: { videos: number };
  memberships: MembershipRow[];
  access: Access;
}

export interface VideoRow {
  id: string;
  shareId: string;
  title: string;
  status: VideoStatus;
  kind: 'screen' | 'audio';
  workspace: 'personal' | 'team';
  /** The privacy scale, widest first. See slate-api's Visibility enum. */
  visibility: 'public' | 'signedIn' | 'team' | 'invited' | 'private';
  durationSec: number | null;
  sizeBytes: number | null;
  createdAt: string;
  readyAt: string | null;
  deletedAt: string | null;
  submitterName: string | null;
  /** The poster frame, from the CDN; null until the recorder's first frame lands. */
  posterUrl: string | null;
  owner: UserRef | null;
  org: OrgRef | null;
  _count: { views: number; comments: number; reactions: number };
}

export type Visibility = VideoRow['visibility'];

export interface VideoSettings {
  visibility: Visibility;
  captionsOn: boolean;
  playbackRate: number;
  emailGate: 'none' | 'ask' | 'require';
  commentsEnabled: boolean;
  reactionsEnabled: boolean;
  downloadEnabled: boolean;
  analyticsPublic: boolean;
  transcriptPublic: boolean;
  linksAtEnd: boolean;
}

export interface VideoComment {
  id: string;
  authorName: string;
  bodyText: string;
  timecodeSec: number | null;
  createdAt: string;
  resolvedAt: string | null;
  authorUser: (UserRef & { avatarUrl: string | null }) | null;
}

export interface VideoReaction {
  id: string;
  kind: string;
  timecodeSec: number;
  createdAt: string;
  authorUser: UserRef | null;
}

/** One recording whole: what the share page shows its owner, plus who said what. */
export interface VideoDetail extends Omit<VideoRow, 'owner'> {
  owner: (UserRef & { avatarUrl: string | null }) | null;
  summary: string | null;
  titleSource: 'default' | 'ai' | 'user';
  summarySource: 'default' | 'ai' | 'user' | null;
  width: number | null;
  height: number | null;
  updatedAt: string;
  requestLinkId: string | null;
  editVersion: number;
  renderingVersion: number | null;
  shareUrl: string;
  settings: VideoSettings;
  playback: { hlsUrl: string | null; mp4Url: string | null; captionsUrl: string | null } | null;
  /** The upload itself, signed, while the ladder is still being cut. */
  rawPlaybackUrl: string | null;
  transcript: {
    status: 'pending' | 'ready' | 'failed' | 'skipped';
    source: string;
    language: string;
    text: string | null;
    captionsUrl: string | null;
    updatedAt: string;
  } | null;
  comments: VideoComment[];
  reactions: VideoReaction[];
  reactionTotals: Record<string, number>;
  tags: { id: string; name: string; scope: 'team' | 'personal' }[];
}

export interface UserDetail extends UserRow {
  updatedAt: string;
  firebaseUid: string;
  _count: { videos: number; requestLinks: number; comments: number; deviceSessions: number };
  videos: VideoRow[];
}

export interface OrgRow extends OrgRef {
  subscriptionStatus: SubscriptionStatus;
  comp: Comp | null;
  planLive: boolean;
  seats: SeatBreakdown;
  billingInterval: 'month' | 'year';
  seatCount: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  trialEndsAt: string | null;
  renewsAt: string | null;
  cancelAt: string | null;
  createdAt: string;
  memberCount: number;
  pendingInvites: number;
  seatsUsed: number;
  _count: { videos: number };
}

export interface OrgDetail extends OrgRef {
  subscriptionStatus: SubscriptionStatus;
  comp: Comp | null;
  planLive: boolean;
  seats: SeatBreakdown;
  billingInterval: 'month' | 'year';
  seatCount: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  trialEndsAt: string | null;
  renewsAt: string | null;
  cancelAt: string | null;
  createdAt: string;
  updatedAt: string;
  memberships: Array<{
    id: string;
    role: Role;
    seat: Seat;
    status: 'active' | 'suspended';
    createdAt: string;
    user: UserRef & { avatarUrl: string | null };
  }>;
  invites: Array<{
    id: string;
    email: string;
    name: string | null;
    role: Role;
    seat: Seat;
    expiresAt: string;
    createdAt: string;
  }>;
  videos: VideoRow[];
  _count: { videos: number; requestLinks: number };
}

/**
 * An invitation to somebody who has never used Slate, comped: the paid plan
 * for them alone, or a team of their own with seats on it. The account, and
 * the team, exist from the moment the invitation does; this is the email's
 * side of it. Keep in step with compInviteOut in slate-api's admin routes.
 */
export type CompInviteKind = 'user' | 'team';

export interface CompInviteRow {
  id: string;
  kind: CompInviteKind;
  email: string;
  name: string | null;
  /** The admin who sent it, by email. */
  invitedBy: string;
  /** When the link in the email stops signing them in. A resend moves it out. */
  expiresAt: string;
  /** When they first came in through the link. */
  acceptedAt: string | null;
  createdAt: string;
  /**
   * The page the email points at, without the sign-in code: safe to paste
   * anywhere, since on its own it can only offer to send the email. The link
   * that signs them in comes from `mintCompInviteLink`, on request.
   */
  startUrl: string;
  /** The email: last successful send, last failure, how many went out. */
  sent: { at: string | null; error: string | null; count: number };
  /** In, one way or another: through the link, or by finishing the welcome steps. */
  signedIn: boolean;
  user: UserRef & { onboardedAt: string | null; comp: Comp | null };
  org: (OrgRef & { subscriptionStatus: SubscriptionStatus; seatCount: number } & OrgComp) | null;
}

/**
 * What the form sends. Every invitation makes a team: of one unless `seats`
 * says otherwise, named after the person unless `team.name` says otherwise.
 */
export interface CompInviteBody {
  email: string;
  name: string | null;
  until: string | null;
  reason: string | null;
  team: { name: string | null; seats: number };
}

/** A comped team for somebody who already has an account and no team. */
export interface CompTeamBody {
  name: string | null;
  seats: number;
  until: string | null;
  reason: string | null;
}

export interface FeatureRequest {
  id: string;
  title: string;
  body: string;
  contact: string;
  videoUrl: string | null;
  source: string;
  createdAt: string;
  handledAt: string | null;
}

export interface AdminRow {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  signedIn: boolean;
  /** The "you are an admin" email: last successful send, last failure, how many went out. */
  invite: { sentAt: string | null; error: string | null; count: number };
  addedBy: UserRef | null;
}

/** One place for every React Query cache key, so invalidation is not guesswork. */
export const qk = {
  me: () => ['admin', 'me'] as const,
  stats: () => ['admin', 'stats'] as const,
  users: (q: string) => ['admin', 'users', q] as const,
  user: (id: string) => ['admin', 'user', id] as const,
  orgs: (q: string, status: string) => ['admin', 'orgs', q, status] as const,
  org: (id: string) => ['admin', 'org', id] as const,
  videos: (q: string, status: string, deleted: boolean) => ['admin', 'videos', q, status, deleted] as const,
  video: (id: string) => ['admin', 'video', id] as const,
  featureRequests: (handled: string) => ['admin', 'feature-requests', handled] as const,
  admins: () => ['admin', 'admins'] as const,
  compInvites: () => ['admin', 'comp-invites'] as const,
};

export const fetchMe = () => api<AdminMe>('/v1/admin/me');
export const fetchStats = () => api<Stats>('/v1/admin/stats');

export const fetchUsers = (q: string, cursor: string | null) =>
  api<Page<UserRow>>(`/v1/admin/users${qs({ q, cursor })}`);
export const fetchUser = (id: string) => api<UserDetail>(`/v1/admin/users/${id}`);

/** Support fixing what a person typed, or did not: a blank name clears it. */
export const updateUser = (id: string, body: { name?: string | null }) =>
  api<UserRow>(`/v1/admin/users/${id}`, { method: 'PATCH', body });

/** The same JSON upload the app itself does: the picture as base64. */
export async function uploadUserAvatar(id: string, blob: Blob): Promise<UserRow> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('That file did not read.'));
    reader.readAsDataURL(blob);
  });
  return api<UserRow>(`/v1/admin/users/${id}/avatar`, { method: 'PUT', body: { contentType: blob.type, data } });
}

export const removeUserAvatar = (id: string) => api<UserRow>(`/v1/admin/users/${id}/avatar`, { method: 'DELETE' });

/** What a grant is worth writing down: when it ends, and why it was given. */
export interface CompBody {
  until: string | null;
  reason: string | null;
  /** Teams only: seats the grant covers, on top of any the team buys. */
  seats?: number | null;
}

/** PUT is the whole grant, so sending it again edits the expiry or the reason. */
export const grantUserComp = (id: string, body: CompBody) =>
  api<Access>(`/v1/admin/users/${id}/comp`, { method: 'PUT', body });
export const revokeUserComp = (id: string) => api<Access>(`/v1/admin/users/${id}/comp`, { method: 'DELETE' });

/** Their own team, comped, with them as owner. 409 `already_in_org` when they have one. */
export const createUserTeam = (id: string, body: CompTeamBody) =>
  api<OrgRef & { subscriptionStatus: SubscriptionStatus; seatCount: number } & OrgComp>(`/v1/admin/users/${id}/team`, {
    method: 'POST',
    body,
  });

export const fetchOrgs = (q: string, status: string, cursor: string | null) =>
  api<Page<OrgRow>>(`/v1/admin/orgs${qs({ q, status, cursor })}`);
export const fetchOrg = (id: string) => api<OrgDetail>(`/v1/admin/orgs/${id}`);

export const grantOrgComp = (id: string, body: CompBody) =>
  api<OrgComp>(`/v1/admin/orgs/${id}/comp`, { method: 'PUT', body });
export const revokeOrgComp = (id: string) => api<OrgComp>(`/v1/admin/orgs/${id}/comp`, { method: 'DELETE' });

export const fetchVideos = (q: string, status: string, deleted: boolean, cursor: string | null) =>
  api<Page<VideoRow>>(`/v1/admin/videos${qs({ q, status, deleted: deleted ? '1' : undefined, cursor })}`);
export const fetchVideo = (id: string) => api<VideoDetail>(`/v1/admin/videos/${id}`);

export const fetchFeatureRequests = (handled: string, cursor: string | null) =>
  api<Page<FeatureRequest>>(`/v1/admin/feature-requests${qs({ handled, cursor })}`);
export const setFeatureRequestHandled = (id: string, handled: boolean) =>
  api<FeatureRequest>(`/v1/admin/feature-requests/${id}`, { method: 'PATCH', body: { handled } });

export const fetchAdmins = () => api<{ items: AdminRow[] }>('/v1/admin/admins');
export const addAdmin = (body: { email: string; name: string | null }) =>
  api<AdminRow>('/v1/admin/admins', { method: 'POST', body });
export const removeAdmin = (id: string) => api<void>(`/v1/admin/admins/${id}`, { method: 'DELETE' });
export const resendAdminInvite = (id: string) => api<AdminRow>(`/v1/admin/admins/${id}/invite`, { method: 'POST' });

export const fetchCompInvites = (cursor: string | null) =>
  api<Page<CompInviteRow>>(`/v1/admin/comp-invites${qs({ cursor })}`);
export const createCompInvite = (body: CompInviteBody) =>
  api<CompInviteRow>('/v1/admin/comp-invites', { method: 'POST', body });
/**
 * The emailed link, made again for the admin to hand over. It signs the
 * reader in as the invited address, and minting it retires the code in the
 * email. `signsIn` is false where the API cannot mint a code; the link then
 * only opens the page.
 */
export const mintCompInviteLink = (id: string) =>
  api<{ url: string; signsIn: boolean; email: string }>(`/v1/admin/comp-invites/${id}/link`, { method: 'POST' });
export const resendCompInvite = (id: string) =>
  api<CompInviteRow>(`/v1/admin/comp-invites/${id}/resend`, { method: 'POST' });
/** What went with it: the link always, the account and the team only if nobody ever came in. */
export const withdrawCompInvite = (id: string) =>
  api<{ removed: { invite: boolean; account: boolean; team: boolean } }>(`/v1/admin/comp-invites/${id}`, {
    method: 'DELETE',
  });
